// src/services/assignmentAPI.ts
// ✅ LOGGING: Complete migration to centralized logging system
// ✅ ENVIRONMENT: Using environment configuration
// ✅ FIXED: Return assignment now uses PUT with status: 'Returned' (matches lambda expectations)

import { authService } from './authService';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';
import { 
  Assignment, 
  CreateAssignmentRequest, 
  ReturnAssignmentRequest,
  AssignmentFilters,
  AssignmentAPIResponse,
  AssignmentListResponse,
  OfficerEquipmentResponse,
  ItemAssignmentHistory,
  UpdateAssignmentRequest,
  ReturnRequest
} from '../types/assignment';

// ✅ LOGGING: Create dedicated logger for Assignment API
const assignmentAPILogger = logger.createServiceLogger('AssignmentAPI');

class AssignmentAPI {
  private baseUrl: string;

  constructor() {
    // Get base URL from environment configuration
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * ✅ UPDATED: Simplified request method using authService with structured logging
   */
  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<AssignmentAPIResponse<T>> {
    const timer = logger.startTimer('AssignmentAPI', 'makeRequest');
    const url = `${this.baseUrl}${endpoint}`;
    
    assignmentAPILogger.info('Making assignment API request', {
      endpoint,
      method: options.method || 'GET',
      hasBody: !!options.body,
      timestamp: new Date().toISOString()
    });

    try {
      // ✅ Use new auth service - automatically gets fresh token
      const response = await authService.makeAuthenticatedRequest(url, options);
      
      assignmentAPILogger.info('Assignment API response received', {
        endpoint,
        status: response.status,
        statusText: response.statusText
      });
      
      const data = await response.json();
      assignmentAPILogger.debug('Assignment API response data parsed', {
        endpoint,
        hasData: !!data,
        dataKeys: Object.keys(data)
      });
      
      if (!response.ok) {
        assignmentAPILogger.error('Assignment API request failed', {
          endpoint,
          status: response.status,
          statusText: response.statusText,
          errorData: data
        });
        
        timer();
        return {
          success: false,
          error: data.error || { code: 'HTTP_ERROR', message: `HTTP ${response.status}: ${data.message || 'Unknown error'}` }
        };
      }

      // Handle different response formats from Lambda
      if (data.assignment) {
        // Single assignment response
        assignmentAPILogger.debug('Single assignment response format detected', { endpoint });
        timer();
        return {
          success: true,
          data: data.assignment as T
        };
      } else if (data.assignments) {
        // Assignment list response
        assignmentAPILogger.debug('Assignment list response format detected', {
          endpoint,
          assignmentCount: data.assignments.length
        });
        timer();
        return {
          success: true,
          data: {
            assignments: data.assignments,
            count: data.count || data.assignments.length,
            total: data.count || data.assignments.length,
            totalCount: data.count || data.assignments.length,
            hasMore: false
          } as T
        };
      } else if (data.history) {
        // History response
        assignmentAPILogger.debug('Assignment history response format detected', {
          endpoint,
          historyCount: data.history.length
        });
        timer();
        return {
          success: true,
          data: {
            assignments: data.history,
            count: data.count || data.history.length
          } as T
        };
      } else if (data.message) {
        // Success message response (like delete operations)
        assignmentAPILogger.debug('Success message response format detected', {
          endpoint,
          message: data.message
        });
        timer();
        return {
          success: true,
          data: data as T
        };
      }

      // Default return for other response formats
      assignmentAPILogger.debug('Default response format handling', { endpoint });
      timer();
      return {
        success: true,
        data: data as T
      };

    } catch (error: any) {
      timer();
      assignmentAPILogger.error('Assignment API request error', {
        endpoint,
        method: options.method || 'GET',
        error: error.message,
        errorCode: error.code
      }, error);
      
      // Handle auth-specific errors from authService
      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' }
        };
      }
      
      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' }
        };
      }

      if (error.code === 'NETWORK_ERROR') {
        return {
          success: false,
          error: { code: 'NETWORK_ERROR', message: error.message }
        };
      }

      return {
        success: false,
        error: { code: 'REQUEST_ERROR', message: error.message || 'Request failed' }
      };
    }
  }

  // Get all assignments with optional filtering
  async getAssignments(filters?: AssignmentFilters): Promise<AssignmentAPIResponse<AssignmentListResponse>> {
    let endpoint = '/assignments';
    
    if (filters) {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.assignedToUserId) params.append('assignedToUserId', filters.assignedToUserId);
      if (filters.inventoryItemId) params.append('inventoryItemId', filters.inventoryItemId);
      if (filters.assignmentReason) params.append('assignmentReason', filters.assignmentReason);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.lastEvaluatedKey) params.append('lastEvaluatedKey', filters.lastEvaluatedKey);
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
    }

    assignmentAPILogger.info('Fetching assignments with filters', {
      hasFilters: !!filters,
      filterCount: filters ? Object.keys(filters).length : 0,
      status: filters?.status || 'all',
      assignedToUserId: filters?.assignedToUserId || null,
      inventoryItemId: filters?.inventoryItemId || null
    });

    return this.makeRequest<AssignmentListResponse>(endpoint);
  }

  // Create new assignment
  async createAssignment(assignmentData: CreateAssignmentRequest): Promise<AssignmentAPIResponse<Assignment>> {
    // ✅ FIXED: Access nested assignment object properties
    assignmentAPILogger.info('Creating new assignment', {
      inventoryItemId: assignmentData.assignment.inventoryItemId,
      assignedToUserId: assignmentData.assignment.assignedToUserId,
      assignmentReason: assignmentData.assignment.assignmentReason || 'Initial Issue',
      hasNotes: !!assignmentData.assignment.notes,
      timestamp: new Date().toISOString()
    });
    
    const response = await this.makeRequest<Assignment>('/assignments', {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
    
    if (response.success) {
      assignmentAPILogger.info('Assignment created successfully', {
        assignmentId: response.data?.assignmentId,
        inventoryItemId: assignmentData.assignment.inventoryItemId,
        assignedToUserId: assignmentData.assignment.assignedToUserId
      });
    }
    
    return response;
  }

  // Update assignment
  async updateAssignment(assignmentId: string, updates: Partial<Assignment>): Promise<AssignmentAPIResponse<Assignment>> {
    assignmentAPILogger.info('Updating assignment', {
      assignmentId,
      updateFields: Object.keys(updates),
      updateCount: Object.keys(updates).length
    });
    
    const response = await this.makeRequest<Assignment>(`/assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    
    if (response.success) {
      assignmentAPILogger.info('Assignment updated successfully', {
        assignmentId,
        updateCount: Object.keys(updates).length
      });
    }
    
    return response;
  }

  // ✅ FIXED: Return assignment - now uses PUT with status: 'Returned' to match lambda expectations
  async returnAssignment(assignmentId: string, returnData: ReturnAssignmentRequest): Promise<AssignmentAPIResponse<Assignment>> {
    assignmentAPILogger.info('Returning assignment', {
      assignmentId,
      condition: returnData.condition,
      hasNotes: !!returnData.notes,
      timestamp: new Date().toISOString()
    });
    
    // ✅ CRITICAL FIX: Lambda expects PUT method with status: 'Returned' in body
    const requestBody = {
      status: 'Returned',
      returnCondition: returnData.condition,
      returnNotes: returnData.notes || '',
      returnDate: new Date().toISOString()
    };
    
    assignmentAPILogger.debug('Return request body prepared', {
      assignmentId,
      requestBody
    });
    
    const response = await this.makeRequest<Assignment>(`/assignments/${assignmentId}`, {
      method: 'PUT',  // ✅ Changed from DELETE to PUT
      body: JSON.stringify(requestBody),
    });
    
    if (response.success) {
      assignmentAPILogger.info('Assignment returned successfully', {
        assignmentId,
        condition: returnData.condition
      });
    } else {
      assignmentAPILogger.error('Assignment return failed', {
        assignmentId,
        condition: returnData.condition,
        error: response.error
      });
    }
    
    return response;
  }

  // Get all equipment for specific officer
  async getOfficerEquipment(userId: string): Promise<AssignmentAPIResponse<OfficerEquipmentResponse>> {
    assignmentAPILogger.info('Fetching officer equipment', {
      userId,
      operation: 'getOfficerEquipment'
    });
    
    const response = await this.makeRequest<OfficerEquipmentResponse>(`/assignments/officer/${userId}`);
    
    if (response.success && response.data) {
      assignmentAPILogger.info('Officer equipment fetched successfully', {
        userId,
        equipmentCount: response.data.assignments?.length || 0
      });
    }
    
    return response;
  }

  // Get assignment history for item
  async getItemHistory(itemId: string): Promise<AssignmentAPIResponse<ItemAssignmentHistory>> {
    assignmentAPILogger.info('Fetching item assignment history', {
      itemId,
      operation: 'getItemHistory'
    });
    
    const response = await this.makeRequest<ItemAssignmentHistory>(`/assignments/item/${itemId}/history`);
    
    if (response.success && response.data) {
      assignmentAPILogger.info('Item history fetched successfully', {
        itemId,
        historyCount: response.data.assignments?.length || 0
      });
    }
    
    return response;
  }

  // Get single assignment
  async getAssignment(assignmentId: string): Promise<AssignmentAPIResponse<Assignment>> {
    assignmentAPILogger.info('Fetching single assignment', {
      assignmentId,
      operation: 'getAssignment'
    });
    
    const response = await this.makeRequest<Assignment>(`/assignments/${assignmentId}`);
    
    if (response.success) {
      assignmentAPILogger.debug('Assignment fetched successfully', {
        assignmentId,
        hasData: !!response.data
      });
    }
    
    return response;
  }

  // Search assignments (advanced search with multiple criteria)
  async searchAssignments(searchParams: any): Promise<AssignmentAPIResponse<AssignmentListResponse>> {
    const endpoint = '/assignments/search';
    
    assignmentAPILogger.info('Searching assignments with criteria', {
      searchParamKeys: Object.keys(searchParams),
      paramCount: Object.keys(searchParams).length,
      operation: 'searchAssignments'
    });
    
    const response = await this.makeRequest<AssignmentListResponse>(endpoint, {
      method: 'POST',
      body: JSON.stringify(searchParams),
    });
    
    if (response.success && response.data) {
      assignmentAPILogger.info('Assignment search completed', {
        resultCount: response.data.assignments?.length || 0,
        paramCount: Object.keys(searchParams).length
      });
    }
    
    return response;
  }

  // Get assignment analytics/summary
  async getAssignmentSummary(): Promise<AssignmentAPIResponse<any>> {
    assignmentAPILogger.info('Fetching assignment summary analytics');
    
    const response = await this.makeRequest<any>('/assignments/summary');
    
    if (response.success) {
      assignmentAPILogger.info('Assignment summary fetched successfully');
    }
    
    return response;
  }

  // Bulk assignment operations
  async bulkAssign(bulkRequest: any): Promise<AssignmentAPIResponse<any>> {
    assignmentAPILogger.info('Starting bulk assignment operation', {
      requestKeys: Object.keys(bulkRequest),
      operation: 'bulkAssign'
    });
    
    const response = await this.makeRequest<any>('/assignments/bulk', {
      method: 'POST',
      body: JSON.stringify(bulkRequest),
    });
    
    if (response.success) {
      assignmentAPILogger.info('Bulk assignment operation completed successfully');
    }
    
    return response;
  }

  // Helper methods for common use cases
  async getActiveAssignments(): Promise<AssignmentAPIResponse<AssignmentListResponse>> {
    assignmentAPILogger.debug('Fetching active assignments only');
    return this.getAssignments({ status: 'Active' });
  }

  async getAssignmentsByOfficer(userId: string): Promise<AssignmentAPIResponse<AssignmentListResponse>> {
    assignmentAPILogger.debug('Fetching assignments by officer', { userId });
    return this.getAssignments({ assignedToUserId: userId });
  }

  async getAssignmentsByItem(itemId: string): Promise<AssignmentAPIResponse<AssignmentListResponse>> {
    assignmentAPILogger.debug('Fetching assignments by item', { itemId });
    return this.getAssignments({ inventoryItemId: itemId });
  }

  // Compatibility methods for existing UI components
  async getAssignmentsCompat(): Promise<Assignment[]> {
    const timer = logger.startTimer('AssignmentAPI', 'getAssignmentsCompat');
    
    try {
      assignmentAPILogger.debug('Using compatibility method for assignments');
      const response = await this.getAssignments();
      
      if (response.success && response.data) {
        assignmentAPILogger.debug('Compatibility assignments fetched', {
          assignmentCount: response.data.assignments.length
        });
        timer();
        return response.data.assignments;
      }
      
      timer();
      throw new Error(response.error?.message || 'Failed to fetch assignments');
    } catch (error) {
      timer();
      assignmentAPILogger.error('Compatibility method failed', {
        method: 'getAssignmentsCompat',
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  }

  async getItemHistoryCompat(itemId: string): Promise<Assignment[]> {
    const timer = logger.startTimer('AssignmentAPI', 'getItemHistoryCompat');
    
    try {
      assignmentAPILogger.debug('Using compatibility method for item history', { itemId });
      const response = await this.getItemHistory(itemId);
      
      if (response.success && response.data) {
        assignmentAPILogger.debug('Compatibility item history fetched', {
          itemId,
          historyCount: response.data.assignments.length
        });
        timer();
        return response.data.assignments;
      }
      
      timer();
      throw new Error(response.error?.message || 'Failed to fetch item history');
    } catch (error) {
      timer();
      assignmentAPILogger.error('Compatibility method failed', {
        method: 'getItemHistoryCompat',
        itemId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  }

  async getOfficerEquipmentCompat(userId: string): Promise<Assignment[]> {
    const timer = logger.startTimer('AssignmentAPI', 'getOfficerEquipmentCompat');
    
    try {
      assignmentAPILogger.debug('Using compatibility method for officer equipment', { userId });
      const response = await this.getOfficerEquipment(userId);
      
      if (response.success && response.data) {
        assignmentAPILogger.debug('Compatibility officer equipment fetched', {
          userId,
          equipmentCount: response.data.assignments.length
        });
        timer();
        return response.data.assignments;
      }
      
      timer();
      throw new Error(response.error?.message || 'Failed to fetch officer equipment');
    } catch (error) {
      timer();
      assignmentAPILogger.error('Compatibility method failed', {
        method: 'getOfficerEquipmentCompat',
        userId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  }
}

export const assignmentAPI = new AssignmentAPI();
